import { renderPageShell } from '../lib/page-shell';
import { authorizedFetch, getToken } from '../lib/session';
import type { RouteHandler } from '../lib/router';

// Redesigned 2026-08-10 (owner decision): a clean progression view, not a
// flat list — every achievement is visible (not just unlocked ones), grouped
// into its family's tier ladder where one exists. Locked tiers show greyed
// but visible so the ladder reads as a whole; secret achievements go further
// and blank out description/XP/reward until earned, name only until then
// (server-enforced — see apps/worker/src/achievements-routes.ts, never sent
// to the client at all for a still-secret one). Level-milestone rewards are
// "essentially just achievements" per that same decision, so they render as
// their own ladder alongside real achievements, not tucked away in Inventory.
// Inventory itself is owned-only now — this page is where you see what's
// still ahead.

interface AchievementFamily {
  familySlug: string;
  familyName: string;
  tier: number;
  tierLabel: string;
  tierCount: number;
}

interface RewardItemPreview {
  itemId: string;
  name: string;
  slot: string;
  image: string | null;
}

interface AchievementView {
  id: string;
  name: string;
  description: string | null;
  xp: number | null;
  rewardSoft: number | null;
  hidden: boolean;
  unlocked: boolean;
  unlockedAt: number | null;
  family: AchievementFamily | null;
  rewardItems: RewardItemPreview[] | null;
}

interface LevelRewardView {
  itemId: string;
  name: string;
  description: string;
  slot: string;
  rarity: string;
  unlockLevel: number;
  unlocked: boolean;
  image: string | null;
}

interface AchievementsResponse {
  achievements: AchievementView[];
  levelRewards: LevelRewardView[];
  level: number;
  newlyEarned: string[];
}

export const renderAchievements: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'account', {
    title: 'ACHIEVEMENTS',
    subtitle: 'Every achievement and level milestone. Unlocked tiers in full, what’s next greyed out, secrets blanked until you find them.',
    body: `<div id="achievements-mount"><p class="attempt-list__empty">Loading…</p></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#achievements-mount')!;

  if (!getToken()) {
    mount.innerHTML = `<p class="settings-hint">Sign in on the Account page to track achievements.</p>`;
    return;
  }

  let openPopover: { el: HTMLElement; backdrop: HTMLElement } | null = null;

  function closePopover() {
    if (!openPopover) return;
    openPopover.el.remove();
    openPopover.backdrop.remove();
    openPopover = null;
  }

  function openPopoverFor(anchor: HTMLElement, kind: 'achievement', item: AchievementView): void;
  function openPopoverFor(anchor: HTMLElement, kind: 'level', item: LevelRewardView): void;
  function openPopoverFor(anchor: HTMLElement, kind: 'achievement' | 'level', item: AchievementView | LevelRewardView) {
    closePopover();

    const isAchievement = kind === 'achievement';
    const a = isAchievement ? (item as AchievementView) : null;
    const l = !isAchievement ? (item as LevelRewardView) : null;

    const name = a?.name ?? l!.name;
    const unlocked = a?.unlocked ?? l!.unlocked;
    const secretLocked = Boolean(a?.hidden && !a.unlocked);
    const description = secretLocked ? null : (a?.description ?? l!.description);
    const rewardItems: RewardItemPreview[] = a?.rewardItems ?? (l ? [{ itemId: l.itemId, name: l.name, slot: l.slot, image: l.image }] : []);
    const xp = a ? a.xp : null;
    const rewardSoft = a ? a.rewardSoft : null;
    const requirement = l ? `Reach level ${l.unlockLevel}` : null;

    const backdrop = document.createElement('div');
    backdrop.className = 'tier-popover-backdrop';
    backdrop.addEventListener('click', closePopover);

    const popover = document.createElement('div');
    popover.className = 'tier-popover';
    popover.innerHTML = `
      <div class="tier-popover__head">
        <strong>${name}</strong>
        <span class="achievement__${unlocked ? 'date' : 'locked'}">${unlocked ? 'Unlocked' : secretLocked ? 'Secret' : 'Locked'}</span>
      </div>
      ${description ? `<p>${description}</p>` : secretLocked ? `<p class="settings-hint">Not yet found. Find it in-game.</p>` : ''}
      ${requirement ? `<p class="settings-hint">${requirement}</p>` : ''}
      ${
        !secretLocked && (xp !== null || rewardSoft)
          ? `<p class="settings-hint">${[xp !== null ? `+${xp} XP` : null, rewardSoft ? `+${rewardSoft} Ticks` : null].filter(Boolean).join(' · ')}</p>`
          : ''
      }
      ${
        !secretLocked && rewardItems.length > 0
          ? rewardItems
              .map(
                (ri) => `
                  <div class="tier-popover__reward">
                    ${ri.image ? `<div class="tier-popover__thumb" style="background-image:url('${ri.image}');"></div>` : ''}
                    <span>${ri.name}</span>
                  </div>
                `,
              )
              .join('')
          : ''
      }
    `;

    document.body.appendChild(backdrop);
    document.body.appendChild(popover);

    // Anchor near the clicked tile, clamped so it never runs off-screen.
    const rect = anchor.getBoundingClientRect();
    const popRect = popover.getBoundingClientRect();
    const margin = 8;
    let left = rect.left;
    let top = rect.bottom + margin;
    if (left + popRect.width > window.innerWidth - margin) left = window.innerWidth - popRect.width - margin;
    if (left < margin) left = margin;
    if (top + popRect.height > window.innerHeight - margin) top = rect.top - popRect.height - margin;
    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;

    openPopover = { el: popover, backdrop };
  }

  function tierTile(kind: 'achievement' | 'level', id: string, label: string, name: string, unlocked: boolean, secretLocked: boolean): string {
    const stateClass = unlocked ? 'is-unlocked' : secretLocked ? 'is-secret' : 'is-locked';
    return `
      <button class="tier-tile ${stateClass}" type="button" data-kind="${kind}" data-id="${id}">
        <span class="tier-tile__label">${label}</span>
        <span class="tier-tile__name">${name}</span>
      </button>
    `;
  }

  async function render() {
    const data = await authorizedFetch<AchievementsResponse>('/api/achievements');

    // Secret achievements get their own section, not scattered through
    // family ladders or lumped into "Other" (owner decision, 2026-08-10) —
    // pulled out first, regardless of family, before anything else groups.
    const secrets: AchievementView[] = [];
    const families = new Map<string, { familyName: string; tierCount: number; entries: AchievementView[] }>();
    const standalone: AchievementView[] = [];
    for (const a of data.achievements) {
      if (a.hidden) {
        secrets.push(a);
      } else if (a.family) {
        const key = a.family.familySlug;
        if (!families.has(key)) families.set(key, { familyName: a.family.familyName, tierCount: a.family.tierCount, entries: [] });
        families.get(key)!.entries.push(a);
      } else {
        standalone.push(a);
      }
    }
    for (const f of families.values()) f.entries.sort((x, y) => (x.family!.tier ?? 0) - (y.family!.tier ?? 0));

    const unlockedCount = data.achievements.filter((a) => a.unlocked).length;
    const progressPct = data.achievements.length > 0 ? Math.round((unlockedCount / data.achievements.length) * 100) : 0;

    const familySections = [...families.entries()]
      .sort((a, b) => a[1].familyName.localeCompare(b[1].familyName))
      .map(
        ([, f]) => `
          <section class="achievement-family">
            <h3>${f.familyName}</h3>
            <div class="tier-row">
              ${f.entries.map((a) => tierTile('achievement', a.id, a.family!.tierLabel, a.name, a.unlocked, false)).join('')}
            </div>
          </section>
        `,
      )
      .join('');

    const standaloneSection = standalone.length
      ? `
        <section class="achievement-family">
          <h3>Other Achievements</h3>
          <div class="tier-row tier-row--wrap">
            ${standalone.map((a) => tierTile('achievement', a.id, '★', a.name, a.unlocked, false)).join('')}
          </div>
        </section>
      `
      : '';

    const secretsSection = secrets.length
      ? `
        <section class="achievement-family">
          <h3>Secret Achievements</h3>
          <div class="tier-row tier-row--wrap">
            ${secrets.map((a) => tierTile('achievement', a.id, a.unlocked ? '★' : '?', a.name, a.unlocked, !a.unlocked)).join('')}
          </div>
        </section>
      `
      : '';

    const levelSection = `
      <section class="achievement-family">
        <h3>Level Rewards <span class="settings-hint">(you're level ${data.level})</span></h3>
        <div class="tier-row tier-row--wrap">
          ${data.levelRewards.map((l) => tierTile('level', l.itemId, `Lv.${l.unlockLevel}`, l.name, l.unlocked, false)).join('')}
        </div>
      </section>
    `;

    mount.innerHTML = `
      <div class="progress-bar" role="progressbar" aria-valuenow="${progressPct}" aria-valuemin="0" aria-valuemax="100">
        <div class="progress-bar__fill" style="width:${progressPct}%;"></div>
      </div>
      <p class="settings-hint">${unlockedCount} of ${data.achievements.length} achievements unlocked (${progressPct}%).</p>
      ${familySections}
      ${standaloneSection}
      ${secretsSection}
      ${levelSection}
    `;

    mount.querySelectorAll<HTMLButtonElement>('.tier-tile').forEach((tile) => {
      tile.addEventListener('click', (event) => {
        event.stopPropagation();
        const kind = tile.dataset.kind as 'achievement' | 'level';
        const id = tile.dataset.id!;
        if (kind === 'achievement') {
          const found = data.achievements.find((a) => a.id === id);
          if (found) openPopoverFor(tile, 'achievement', found);
        } else {
          const found = data.levelRewards.find((l) => l.itemId === id);
          if (found) openPopoverFor(tile, 'level', found);
        }
      });
    });
  }

  render().catch(() => {
    mount.innerHTML = `<p class="attempt-list__empty">Could not load achievements.</p>`;
  });
};
